import { useState, useEffect, useRef, useMemo } from "react"
import {
  Plus,
  Search,
  X,
  MoreVertical,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Eye,
  ExternalLink,
  Trash2,
  ChevronRight,
  ChevronDown,
  Calendar,
  LayoutGrid,
  Check,
  CalendarClock,
  CalendarPlus,
  Bot,
  Timer,
  History,
  Mail,
} from "lucide-react"
import { useAuth } from "../contexts/AuthProvider"
import { useUser } from "../contexts/UserContext"
import { useNavigate } from "react-router-dom"
import interviewTrackingService from "../services/interviewTrackingService"
import OrganizationService from "../services/OrganizationService"
import CalendarScheduling from "./CalendarScheduling"
import OrgScheduleInterviewForm from "./OrgScheduleInterviewForm"
import InterviewDetailsPage from "./InterviewDetailsPage"
import DeleteInterviewModal from "./DeleteInterviewModal"
import RescheduleInterviewForm from "./RescheduleInterviewForm"
import ScheduleNextRoundModal from "./ScheduleNextRoundModal"
import ProfileSidebar from "./ProfileSideBar"
import ApplicantViewer from "./ApplicantViewer"
import EmailModal from "./EmailModal"
import toast from "react-hot-toast"
import ProfileIncompleteOverlay from "./ProfileIncompleteOverlay"
import ReactDOM from "react-dom"

// ── Design tokens ─────────────────────────────────────────────────────────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border:       "0.5px solid #e5e7eb",
  borderHover:  "0.5px solid #d1d5db",
  radius:       8,
  radiusCard:   10,
  bg:           "#fff",
  bgPage:       "#f5f5f0",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
}

const MOBILE_BREAKPOINT = 768
const DESKTOP_SIDEBAR_WIDTH = "15rem"
const TABLE_MIN_WIDTH = 1120

const GRID_COLS = "minmax(0,1.3fr) minmax(0,0.45fr) minmax(0,0.9fr) minmax(0,.80fr) minmax(0,0.65fr) minmax(0,1.3fr) minmax(0,1fr) 150px"
const HEADERS   = ["Candidate", "Round", "Interviewer", "Date", "Type", "Status", "Interviewer\nFeedback", ""]

// ── Helpers ───────────────────────────────────────────────────────────────────
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#0ea5e9,#6366f1)",
  "linear-gradient(135deg,#10b981,#0ea5e9)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#ec4899,#8b5cf6)",
  "linear-gradient(135deg,#14b8a6,#6366f1)",
  "linear-gradient(135deg,#f97316,#eab308)",
  "linear-gradient(135deg,#06b6d4,#10b981)",
]
const getAvatarGradient = (name = "") =>
  AVATAR_GRADIENTS[name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_GRADIENTS.length]
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()

const fmtDate = (iv) => {
  if (!iv.scheduledDate) return "—"
  const d = iv.scheduledDate.seconds
    ? new Date(iv.scheduledDate.seconds * 1000)
    : new Date(iv.scheduledDate)
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}
const fmtTime = (iv) => {
  if (!iv.scheduledDate) return ""
  const d = iv.scheduledDate.seconds
    ? new Date(iv.scheduledDate.seconds * 1000)
    : new Date(iv.scheduledDate)
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })
}

// ── Status badge configs ──────────────────────────────────────────────────────
const FEEDBACK_STATUS_MAP = {
  "Interviewer Not Joined": { icon: AlertCircle, text: "Interviewer Not Joined", bg: "#fffbeb", color: "#92400e", border: "#fde68a", spin: false },
  "Candidate Not Joined":   { icon: AlertCircle, text: "Candidate Not Joined",   bg: "#fffbeb", color: "#92400e", border: "#fde68a", spin: false },
  generating:               { icon: Loader2,      text: "Generating",            bg: "#eef2ff", color: "#4338ca", border: "#c7d2fe", spin: true  },
  generated:                { icon: CheckCircle2, text: "Report Ready",          bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0", spin: false },
}
const FEEDBACK_STATUS_DEFAULT = { icon: Clock, text: "Not Conducted", bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb", spin: false }

const getFeedbackConfig = (status) => FEEDBACK_STATUS_MAP[status] || FEEDBACK_STATUS_DEFAULT

const getInterviewerFeedback = (iv) => {
  const isAI = iv?.interviewMode === "ai"
  const val  = isAI ? iv.final_verdict : iv.hiringDecision
  if (isAI) {
    if (!val) return null
    return val === "Yes" || val === "True"
      ? { text: "PASSED", bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0", dot: "#10b981" }
      : { text: "FAILED", bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", dot: "#ef4444" }
  }
  const map = {
    STRONG_HIRE: { text: "STRONG HIRE", bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0", dot: "#10b981" },
    HIRE:        { text: "HIRE",        bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0", dot: "#10b981" },
    NO_HIRE:     { text: "NO HIRE",     bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", dot: "#ef4444" },
  }
  return map[val] || null
}

// ── Categorisation ────────────────────────────────────────────────────────────
const categorise = (interviews) => {
  const upcoming = []
  const awaiting = []
  const past     = []

  const groups = interviews.reduce((acc, iv) => {
    const key = iv.traceId || `single_${iv.id}`
    if (!acc[key]) acc[key] = []
    acc[key].push(iv)
    return acc
  }, {})

  const lastInterviewIds = new Set(
    Object.values(groups).map((group) => {
      const sorted = [...group].sort((a, b) => (b.roundNumber || 1) - (a.roundNumber || 1))
      return sorted[0].id
    }),
  )

  interviews.forEach((iv) => {
    const isCompleted = iv.status === "completed"
    const isPending   = iv.status === "pending_feedback" || iv.feedbackPending
    const hasFeedback = iv.feedbackStatus && iv.feedbackStatus !== "Not Conducted"
    const isConducted = isCompleted || isPending || hasFeedback

    if (!isConducted) {
      upcoming.push(iv)
    } else if (
      isConducted &&
      lastInterviewIds.has(iv.id) &&
      iv.candidateStatus !== "hired" &&
      iv.decision !== "hire" &&
      iv.candidateStatus !== "rejected"
    ) {
      awaiting.push(iv)
    } else {
      past.push(iv)
    }
  })

  const byDateAsc  = (a, b) => (a.scheduledDate?.seconds ?? 0) * 1000 - (b.scheduledDate?.seconds ?? 0) * 1000
  const byDateDesc = (a, b) => (b.scheduledDate?.seconds ?? 0) * 1000 - (a.scheduledDate?.seconds ?? 0) * 1000
  upcoming.sort(byDateAsc)
  awaiting.sort(byDateDesc)
  past.sort(byDateDesc)

  return { upcoming, awaiting, past }
}

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ name }) => (
  <div style={{
    width: 32, height: 32, borderRadius: 7,
    background: getAvatarGradient(name),
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, color: "#fff",
    fontSize: 11, fontWeight: 700, fontFamily: FF, letterSpacing: "0.02em",
  }}>
    {getInitials(name)}
  </div>
)

// ── Action button ─────────────────────────────────────────────────────────────
const ActionBtn = ({ onClick, children, variant = "ghost" }) => {
  const variants = {
    ghost:  { background: T.bgMuted, color: T.textMuted, border: T.border },
    blue:   { background: "#2563eb", color: "#fff",      border: "none"   },
    indigo: { background: "#4f46e5", color: "#fff",      border: "none"   },
  }
  const s = variants[variant] || variants.ghost
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        height: 28, padding: "0 10px",
        borderRadius: T.radius - 1, border: s.border,
        background: s.background, color: s.color,
        fontSize: 12, fontWeight: 700, fontFamily: FF,
        letterSpacing: "0.02em", cursor: "pointer",
        outline: "none", transition: "opacity 0.12s", whiteSpace: "nowrap",
        flexShrink: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.82" }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1" }}
    >
      {children}
    </button>
  )
}

// ── Column header row ─────────────────────────────────────────────────────────
const TableHeader = () => (
  <div style={{
    display: "grid", gridTemplateColumns: GRID_COLS,
    padding: "7px 20px", borderBottom: T.border, background: "#fdfcfb",
  }}>
    {HEADERS.map((h, i) => (
      <span key={i} style={{
        fontSize: 12, fontWeight: 700, color: T.textTertiary,
        textTransform: "uppercase", letterSpacing: "0.07em",
        fontFamily: MONO, whiteSpace: "pre-line", lineHeight: 1.3,
        textAlign: i === HEADERS.length - 1 ? "right" : "left",
        display: "flex", alignItems: "center", minHeight: 36,
      }}>
        {h}
      </span>
    ))}
  </div>
)

// ── Shimmer rows ──────────────────────────────────────────────────────────────
const TableShimmer = ({ rows = 10 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{
        display: "grid", gridTemplateColumns: GRID_COLS,
        padding: "13px 20px",
        borderBottom: i < rows - 1 ? T.border : "none",
        alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, paddingRight: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: 7, background: "#f3f4f6" }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 13, width: "65%", background: "#f3f4f6", borderRadius: 4, marginBottom: 6 }} />
            <div style={{ height: 11, width: "45%", background: "#f9fafb", borderRadius: 4 }} />
          </div>
        </div>
        {[24, 80, 64, 56, 96, 64].map((w, j) => (
          <div key={j} style={{ height: 12, width: w, background: "#f3f4f6", borderRadius: 4 }} />
        ))}
        <div style={{ height: 28, width: 56, background: "#f3f4f6", borderRadius: T.radius, marginLeft: "auto" }} />
      </div>
    ))}
  </>
)

// ── Dropdown item helper ──────────────────────────────────────────────────────
const DropdownItem = ({ icon: Icon, label, color, hoverBg = "#252e40", onClick }) => (
  <button
    className="ivt-menu"
    onClick={onClick}
    style={{
      width: "100%", display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", border: "none",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
      background: "transparent", cursor: "pointer",
      fontFamily: FF, transition: "background 0.1s", outline: "none",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
  >
    <Icon size={13} color={color} style={{ flexShrink: 0 }} />
    <span style={{ fontSize: 12, fontWeight: 700, color, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: MONO }}>
      {label}
    </span>
  </button>
)

// ── Interview row ─────────────────────────────────────────────────────────────
const InterviewRow = ({
  iv,
  section,
  isLast,
  onCardClick,
  onDelete,
  onReschedule,
  onScheduleNext,
  navigate,
  openDropdownId,
  dropdownPos,
  onOpenMenu,
  onCloseMenu,
  onViewApplicant,
  onEmailClick,
}) => {
  const isAI = iv?.interviewMode === "ai"
  const fbCfg = getFeedbackConfig(iv.feedbackStatus)
  const FbIcon = fbCfg.icon
  const ivFb = getInterviewerFeedback(iv)

  return (
    <div
      onClick={() => onCardClick(iv)}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        padding: "11px 20px",
        alignItems: "center",
        borderBottom: isLast ? "none" : T.border,
        cursor: "pointer",
        transition: "background 0.1s",
        background: "transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = T.bgMuted
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent"
      }}
    >
      {/* Candidate */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minWidth: 0,
          paddingRight: 16,
        }}
      >
        <Avatar name={iv.candidateName || iv.candidate || ""} />
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 600,
              color: T.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: FF,
            }}
          >
            {iv.candidateName || iv.candidate || "Unknown Candidate"}
          </p>
          <p
            style={{
              margin: "2px 0 0",
              fontSize: 12,
              fontWeight: 500,
              color: T.textTertiary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: FF,
            }}
          >
            {iv.positionTitle || iv.jobTitle || "—"}
          </p>
        </div>
      </div>

      {/* Round */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: T.text,
            fontFamily: MONO,
          }}
        >
          {String(iv.roundNumber || 1).padStart(2, "0")}
        </span>
      </div>

      {/* Interviewer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          paddingRight: 8,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: T.textMuted,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: FF,
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          {isAI && <Bot size={13} color="#6d28d9" style={{ flexShrink: 0 }} />}
          {iv.interviewerName || iv.interviewer || "—"}
        </span>
      </div>

      {/* Date */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: T.text,
            fontFamily: FF,
          }}
        >
          {fmtDate(iv)}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: T.textTertiary,
            marginTop: 2,
            fontFamily: FF,
          }}
        >
          {fmtTime(iv)}
        </span>
      </div>

      {/* Type badge */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            borderRadius: T.radius,
            background: isAI ? "#f5f3ff" : "#f9fafb",
            color: isAI ? "#6d28d9" : T.textTertiary,
            border: `0.5px solid ${isAI ? "#ddd6fe" : "#e5e7eb"}`,
            fontSize: 12,
            fontWeight: 600,
            fontFamily: MONO,
            whiteSpace: "nowrap",
          }}
        >
          {isAI ? "AI" : "Human + AI"}
        </span>
      </div>

      {/* Status badge */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            borderRadius: T.radius,
            background: fbCfg.bg,
            color: fbCfg.color,
            border: `0.5px solid ${fbCfg.border}`,
            fontSize: 12,
            fontWeight: 600,
            fontFamily: MONO,
            whiteSpace: "nowrap",
          }}
        >
          <FbIcon
            size={10}
            style={
              fbCfg.spin
                ? { animation: "ivt-spin 0.8s linear infinite", flexShrink: 0 }
                : { flexShrink: 0 }
            }
          />
          {fbCfg.text}
        </span>
      </div>

      {/* Interviewer feedback */}
      <div style={{ display: "flex", alignItems: "center" }}>
        {ivFb ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 8px",
              borderRadius: T.radius,
              background: ivFb.bg,
              color: ivFb.color,
              border: `0.5px solid ${ivFb.border}`,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: MONO,
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: ivFb.dot,
                flexShrink: 0,
              }}
            />
            {ivFb.text}
          </span>
        ) : (
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: T.textTertiary,
              fontFamily: FF,
            }}
          >
            —
          </span>
        )}
      </div>

      {/* Actions */}
      <div
        className="ivt-menu"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 5,
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Join — upcoming non-AI only */}
        {section === "upcoming" && !isAI && (
          <ActionBtn
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/meeting/${iv.id}?host=${iv.hostToken}`)
            }}
          >
            Join <ExternalLink size={10} strokeWidth={2} />
          </ActionBtn>
        )}

        {/* Report — awaiting / past */}
        {(section === "awaiting" || section === "past") && (
          <ActionBtn
            variant="blue"
            onClick={(e) => {
              e.stopPropagation()
              onCardClick(iv)
            }}
          >
            Report
          </ActionBtn>
        )}

        {/* View — opens ApplicantViewer */}
        <ActionBtn
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation()
            onViewApplicant({
              orgId: iv.organizationId ?? "",
              jobId: iv.jobListingId ?? "",
              applicantId: iv.applicantId ?? null,
              applicantEmail: iv.candidateEmail ?? null,
            })
          }}
        >
          View <ChevronRight size={10} strokeWidth={2.5} />
        </ActionBtn>

        {/* Email icon button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onEmailClick(iv)
          }}
          title="Send email to candidate"
          style={{
            width: 28,
            height: 28,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: T.bg,
            border: T.border,
            borderRadius: T.radius - 1,
            cursor: "pointer",
            color: T.textTertiary,
            transition: "all 0.12s",
            outline: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#111827"
            e.currentTarget.style.color = "#fff"
            e.currentTarget.style.borderColor = "#111827"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = T.bg
            e.currentTarget.style.color = T.textTertiary
            e.currentTarget.style.borderColor = "#e5e7eb"
          }}
        >
          <Mail size={12} />
        </button>

        {/* Three-dot trigger */}
        <button
          className="ivt-menu"
          onClick={(e) => onOpenMenu(iv.id, e)}
          style={{
            width: 28,
            height: 28,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: T.bg,
            border: T.border,
            borderRadius: T.radius - 1,
            cursor: "pointer",
            color: T.textTertiary,
            transition: "all 0.12s",
            outline: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#111827"
            e.currentTarget.style.color = "#fff"
            e.currentTarget.style.borderColor = "#111827"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = T.bg
            e.currentTarget.style.color = T.textTertiary
            e.currentTarget.style.borderColor = "#e5e7eb"
          }}
        >
          <MoreVertical size={12} />
        </button>

        {/* Portal dropdown */}
        {openDropdownId === iv.id &&
          dropdownPos &&
          ReactDOM.createPortal(
            <div
              className="ivt-menu"
              style={{
                position: "fixed",
                zIndex: 9999,
                top: dropdownPos.top,
                bottom: dropdownPos.bottom,
                right: dropdownPos.right,
                width: 192,
                overflow: "hidden",
                background: "#1e2535",
                borderRadius: T.radiusCard,
                boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              }}
            >
              {!isAI && (
                <DropdownItem
                  icon={ExternalLink}
                  label="Join Meeting"
                  color="#8b95aa"
                  onClick={() => {
                    navigate(`/meeting/${iv.id}?host=${iv.hostToken}`)
                    onCloseMenu()
                  }}
                />
              )}
              <DropdownItem
                icon={Eye}
                label="Report"
                color="#8b95aa"
                onClick={() => {
                  onCardClick(iv)
                  onCloseMenu()
                }}
              />
              {section === "upcoming" && !isAI && (
                <DropdownItem
                  icon={CalendarClock}
                  label="Reschedule"
                  color="#818cf8"
                  onClick={() => {
                    onReschedule(iv)
                    onCloseMenu()
                  }}
                />
              )}
              {section === "awaiting" && (
                <DropdownItem
                  icon={CalendarPlus}
                  label="Schedule Next"
                  color="#34d399"
                  onClick={() => {
                    onScheduleNext(iv)
                    onCloseMenu()
                  }}
                />
              )}
              <DropdownItem
                icon={Trash2}
                label="Delete"
                color="#f87171"
                hoverBg="#2d1f1f"
                onClick={() => {
                  onDelete(iv)
                  onCloseMenu()
                }}
              />
            </div>,
            document.body,
          )}
      </div>
    </div>
  )
}

// ── Section card ──────────────────────────────────────────────────────────────
const SECTION_META = {
  upcoming: { label: "Upcoming interviews", icon: Calendar, accent: { text: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" } },
  awaiting: { label: "Awaiting feedback",   icon: Timer,    accent: { text: "#d97706", bg: "#fffbeb", border: "#fde68a" } },
  past:     { label: "Past interviews",     icon: History,  accent: { text: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" } },
}

const SectionTable = ({
  sectionKey,
  interviews,
  onCardClick,
  onDelete,
  onReschedule,
  onScheduleNext,
  navigate,
  onViewApplicant,
  onEmailClick,
  cardRadius = T.radiusCard,
}) => {
  const [collapsed, setCollapsed] = useState(false)
  const [openDropdownId, setOpenDropdownId] = useState(null)
  const [dropdownPos, setDropdownPos] = useState(null)

  const meta = SECTION_META[sectionKey]
  const Icon = meta.icon

  useEffect(() => {
    const onMouseDown = (e) => {
      if (!e.target.closest(".ivt-menu")) {
        setOpenDropdownId(null)
        setDropdownPos(null)
      }
    }
    const onScroll = () => {
      setOpenDropdownId(null)
      setDropdownPos(null)
    }
    document.addEventListener("mousedown", onMouseDown)
    window.addEventListener("scroll", onScroll, true)
    return () => {
      document.removeEventListener("mousedown", onMouseDown)
      window.removeEventListener("scroll", onScroll, true)
    }
  }, [])

  const openMenu = (id, e) => {
    e.stopPropagation()
    if (openDropdownId === id) {
      setOpenDropdownId(null)
      setDropdownPos(null)
      return
    }
    const r = e.currentTarget.getBoundingClientRect()
    const dropdownHeight = 200
    const spaceBelow = window.innerHeight - r.bottom
    const spaceAbove = r.top
    const rightOffset = window.innerWidth - r.right
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      setDropdownPos({
        bottom: window.innerHeight - r.top + 4,
        top: undefined,
        right: rightOffset,
      })
    } else {
      setDropdownPos({
        top: r.bottom + 4,
        bottom: undefined,
        right: rightOffset,
      })
    }
    setOpenDropdownId(id)
  }

  return (
    <div
      style={{
        background: T.bg,
        borderRadius: cardRadius,
        border: T.border,
        boxShadow: T.shadow,
        overflow: "visible",
      }}
    >
      {/* Section header */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "11px 16px",
          background: T.bgMuted,
          border: "none",
          borderBottom: collapsed ? "none" : T.border,
          borderRadius: collapsed
            ? cardRadius
            : `${cardRadius}px ${cardRadius}px 0 0`,
          cursor: "pointer",
          transition: "background 0.12s",
          outline: "none",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#f3f2f0"
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = T.bgMuted
        }}
      >
        {collapsed ? (
          <ChevronRight size={13} strokeWidth={2.5} color={T.textTertiary} />
        ) : (
          <ChevronDown size={13} strokeWidth={2.5} color={T.textTertiary} />
        )}
        <Icon size={14} color={meta.accent.text} style={{ flexShrink: 0 }} />
        <span
          style={{
            flex: 1,
            textAlign: "left",
            fontSize: 13,
            fontWeight: 700,
            color: meta.accent.text,
            letterSpacing: "-0.01em",
            fontFamily: FF,
          }}
        >
          {meta.label}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: meta.accent.text,
            background: meta.accent.bg,
            border: `0.5px solid ${meta.accent.border}`,
            borderRadius: 5,
            padding: "1px 7px",
            fontFamily: MONO,
          }}
        >
          {interviews.length}
        </span>
      </button>

      {!collapsed && interviews.length > 0 && (
        <div
          style={{
            overflowX: "auto",
            overflowY: "visible",
            borderRadius: `0 0 ${cardRadius}px ${cardRadius}px`,
          }}
        >
          <div style={{ minWidth: TABLE_MIN_WIDTH }}>
            <TableHeader />
            {interviews.map((iv, idx) => (
              <InterviewRow
                key={iv.id}
                iv={iv}
                section={sectionKey}
                isLast={idx === interviews.length - 1}
                onCardClick={onCardClick}
                onDelete={onDelete}
                onReschedule={onReschedule}
                onScheduleNext={onScheduleNext}
                navigate={navigate}
                openDropdownId={openDropdownId}
                dropdownPos={dropdownPos}
                onOpenMenu={openMenu}
                onCloseMenu={() => {
                  setOpenDropdownId(null)
                  setDropdownPos(null)
                }}
                onViewApplicant={onViewApplicant}
                onEmailClick={onEmailClick}
              />
            ))}
          </div>
        </div>
      )}

      {!collapsed && interviews.length === 0 && (
        <div
          style={{
            padding: "20px 20px",
            fontSize: 13,
            fontWeight: 500,
            color: T.textTertiary,
            fontFamily: FF,
            fontStyle: "italic",
            borderRadius: `0 0 ${cardRadius}px ${cardRadius}px`,
          }}
        >
          No interviews in this stage
        </div>
      )}
    </div>
  )
}

// ── Job filter dropdown ───────────────────────────────────────────────────────
const JobFilterDropdown = ({ allJobTitles, allInterviews, selectedJobs, setSelectedJobs }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [])

  const hasFilter = selectedJobs.length > 0

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          height: 34, padding: "0 12px", borderRadius: T.radius,
          border: hasFilter ? "none" : T.border,
          background: hasFilter ? "#111827" : T.bg,
          color: hasFilter ? "#fff" : T.textMuted,
          fontSize: 12, fontWeight: 600, fontFamily: FF,
          cursor: "pointer", outline: "none", transition: "border-color 0.12s, background 0.12s",
        }}
        onMouseEnter={(e) => { if (!hasFilter) e.currentTarget.style.borderColor = "#9ca3af" }}
        onMouseLeave={(e) => { if (!hasFilter) e.currentTarget.style.borderColor = "#e5e7eb" }}
      >
        <LayoutGrid size={12} strokeWidth={2.5} style={{ flexShrink: 0 }} />
        <span>{hasFilter ? `${selectedJobs.length} Job${selectedJobs.length > 1 ? "s" : ""}` : "Job title"}</span>
        {hasFilter ? (
          <span onClick={(e) => { e.stopPropagation(); setSelectedJobs([]) }} style={{ opacity: 0.7, display: "flex", alignItems: "center", cursor: "pointer" }}>
            <X size={10} strokeWidth={2.5} />
          </span>
        ) : (
          <ChevronDown size={11} strokeWidth={2} style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0,
          background: T.bg, border: T.border, borderRadius: T.radiusCard,
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)", zIndex: 500, minWidth: 220, overflow: "hidden",
        }}>
          <div style={{ padding: "8px 14px 6px", borderBottom: T.border }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: T.textTertiary, fontFamily: MONO }}>
              Filter by job title
            </span>
          </div>
          {allJobTitles.length === 0 ? (
            <div style={{ padding: "16px 14px", fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF }}>
              No job titles found
            </div>
          ) : (
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
              {allJobTitles.map((title) => {
                const checked = selectedJobs.includes(title)
                const count   = allInterviews.filter((i) => (i.positionTitle || i.jobTitle) === title).length
                return (
                  <button
                    key={title}
                    onClick={() => setSelectedJobs((prev) => checked ? prev.filter((j) => j !== title) : [...prev, title])}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "9px 14px", border: "none", borderBottom: T.border,
                      background: checked ? T.bgMuted : "transparent",
                      cursor: "pointer", fontFamily: FF, transition: "background 0.1s", outline: "none",
                    }}
                    onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = T.bgMuted }}
                    onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = checked ? T.bgMuted : "transparent" }}
                  >
                    <span style={{ fontSize: 13, fontWeight: checked ? 600 : 500, color: checked ? T.text : T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>
                      {title}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.textTertiary, fontFamily: MONO }}>{count}</span>
                      {checked && <Check size={12} strokeWidth={2.5} color={T.text} />}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Date range picker ─────────────────────────────────────────────────────────
const DateRangePicker = ({ dateRange, setDateRange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [])

  const hasFilter = dateRange.from || dateRange.to

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          height: 34, padding: "0 12px", borderRadius: T.radius,
          border: hasFilter ? "none" : T.border,
          background: hasFilter ? "#111827" : T.bg,
          color: hasFilter ? "#fff" : T.textMuted,
          fontSize: 12, fontWeight: 600, fontFamily: FF,
          cursor: "pointer", outline: "none", transition: "border-color 0.12s, background 0.12s",
        }}
        onMouseEnter={(e) => { if (!hasFilter) e.currentTarget.style.borderColor = "#9ca3af" }}
        onMouseLeave={(e) => { if (!hasFilter) e.currentTarget.style.borderColor = "#e5e7eb" }}
      >
        <Calendar size={12} strokeWidth={2.5} style={{ flexShrink: 0 }} />
        <span>{hasFilter ? `${dateRange.from || "…"} → ${dateRange.to || "…"}` : "Date range"}</span>
        {hasFilter && (
          <span onClick={(e) => { e.stopPropagation(); setDateRange({ from: "", to: "" }) }} style={{ opacity: 0.7, display: "flex", alignItems: "center", cursor: "pointer" }}>
            <X size={10} strokeWidth={2.5} />
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          background: T.bg, border: T.border, borderRadius: T.radiusCard,
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)", zIndex: 500, width: 240, padding: 16,
        }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: T.textTertiary, fontFamily: MONO, marginBottom: 12 }}>
            Date range
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {["from", "to"].map((key) => (
              <div key={key}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textTertiary, fontFamily: MONO, marginBottom: 5 }}>
                  {key === "from" ? "From" : "To"}
                </label>
                <input
                  type="date"
                  value={dateRange[key]}
                  onChange={(e) => setDateRange((p) => ({ ...p, [key]: e.target.value }))}
                  style={{
                    width: "100%", padding: "6px 10px",
                    border: T.border, borderRadius: T.radius,
                    fontSize: 13, fontWeight: 500, color: T.text,
                    fontFamily: FF, background: T.bg,
                    outline: "none", transition: "border-color 0.12s",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "#9ca3af" }}
                  onBlur={(e)  => { e.target.style.borderColor = "#e5e7eb" }}
                />
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
              <button
                onClick={() => { setDateRange({ from: "", to: "" }); setOpen(false) }}
                style={{
                  flex: 1, height: 32, border: T.border, borderRadius: T.radius,
                  background: T.bg, color: T.textMuted,
                  fontSize: 12, fontWeight: 600, fontFamily: FF,
                  cursor: "pointer", outline: "none", transition: "border-color 0.12s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
              >
                Clear
              </button>
              <button
                onClick={() => setOpen(false)}
                style={{
                  flex: 1, height: 32, border: "none", borderRadius: T.radius,
                  background: "#111827", color: "#fff",
                  fontSize: 12, fontWeight: 600, fontFamily: FF,
                  cursor: "pointer", outline: "none", transition: "background 0.12s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#27272a" }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#111827" }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Mine-only toggle ──────────────────────────────────────────────────────────
const MineToggle = ({ value, onChange }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: T.textTertiary, fontFamily: FF, whiteSpace: "nowrap" }}>
      Mine only
    </span>
    <button
      onClick={() => onChange(!value)}
      style={{
        position: "relative", width: 36, height: 20, borderRadius: 10,
        border: T.border, background: value ? "#111827" : "#f3f4f6",
        cursor: "pointer", outline: "none",
        transition: "background 0.2s, border-color 0.2s", flexShrink: 0,
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: value ? 17 : 2,
        width: 14, height: 14, borderRadius: "50%",
        background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
        transition: "left 0.2s",
      }} />
    </button>
  </div>
)

const ViewToggle = ({ isCalendar, onChange }) => (
  <div style={{
    display: "flex", alignItems: "stretch",
    border: T.border, borderRadius: T.radius,
    overflow: "hidden", background: T.bg, height: 34,
  }}>
    {[
      { label: "Grid",     icon: LayoutGrid, active: !isCalendar },
      { label: "Calendar", icon: Calendar,   active: isCalendar  },
    ].map(({ label, icon: Icon, active }, i) => (
      <button
        key={label}
        onClick={() => onChange(label === "Calendar")}
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: "0 12px",
          background: active ? "rgb(245,245,240)" : "transparent", border: "none",
          borderRight: i === 0 ? T.border : "none",
          color: active ? "#111827" : T.textTertiary,
          fontSize: 12, fontWeight: 600, fontFamily: FF,
          cursor: "pointer", outline: "none", transition: "background 0.12s, color 0.12s",
        }}
      >
        <Icon size={11} strokeWidth={1.5} style={{ opacity: active ? 0.8 : 0.5 }} />
        {label}
      </button>
    ))}
  </div>
)

// ── Empty / Error states ──────────────────────────────────────────────────────
const EmptyState = ({ searchQuery, onSchedule, cardRadius = T.radiusCard }) => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "128px 24px",
     textAlign: "center",
  }}>
    <div style={{ width: 44, height: 44, borderRadius: cardRadius, border: T.border, background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, boxShadow: T.shadow }}>
      <Calendar size={18} color="#d1d5db" />
    </div>
    <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6, fontFamily: FF, letterSpacing: "-0.01em" }}>
      {searchQuery ? "No matches found" : "No interviews yet"}
    </p>
    <p style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, marginBottom: 20, fontFamily: FF }}>
      {searchQuery ? "Try a different search term" : "Schedule your first interview to get started"}
    </p>
    {!searchQuery && (
      <button
        onClick={onSchedule}
        style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          height: 36, padding: "0 18px", borderRadius: '36px',
          border: "none", background: "#111827", color: "#fff",
          fontSize: 13, fontWeight: 600, fontFamily: FF,
          cursor: "pointer", transition: "background 0.12s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#27272a" }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#111827" }}
      >
        <Plus size={13} strokeWidth={2.5} />
        Schedule interview
      </button>
    )}
  </div>
)

const ErrorState = ({ error, onRetry, cardRadius = T.radiusCard }) => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "64px 24px",
    border: "0.5px solid #fecaca", borderRadius: cardRadius,
    background: "#fef2f2", textAlign: "center",
  }}>
    <div style={{ width: 44, height: 44, borderRadius: cardRadius, border: "0.5px solid #fecaca", background: "#fff1f2", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
      <X size={18} color="#fca5a5" />
    </div>
    <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6, fontFamily: FF }}>Something went wrong</p>
    <p style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, marginBottom: 20, fontFamily: FF }}>{error}</p>
    <button
      onClick={onRetry}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        height: 32, padding: "0 14px", borderRadius: T.radius,
        border: T.border, background: T.bg, color: T.textMuted,
        fontSize: 12, fontWeight: 600, fontFamily: FF,
        cursor: "pointer", outline: "none", transition: "border-color 0.12s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
    >
      Try again
    </button>
  </div>
)

// ── Calendar shimmer ──────────────────────────────────────────────────────────
const CalendarShimmer = () => (
  <div style={{ padding: 32 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
      <div style={{ height: 20, width: 160, background: "#f3f4f6", borderRadius: 4 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ height: 32, width: 80, background: "#f3f4f6", borderRadius: T.radius }} />
        <div style={{ height: 32, width: 80, background: "#f3f4f6", borderRadius: T.radius }} />
      </div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
      {Array.from({ length: 35 }).map((_, i) => (
        <div key={i} style={{ height: 80, background: "#f3f4f6", borderRadius: T.radius }} />
      ))}
    </div>
  </div>
)

// ── ApplicantViewer slide-over panel ─────────────────────────────────────────
const ApplicantPanel = ({ target, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden"
    const handler = (e) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", handler)
    return () => {
      document.body.style.overflow = ""
      document.removeEventListener("keydown", handler)
    }
  }, [onClose])

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.28)", zIndex: 40,
          backdropFilter: "blur(1px)",
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        style={{
          position: "fixed", right: 0, top: 0,
          height: "100%",
          width: "min(calc(100vw - 18rem), 1100px)",
          background: T.bg,
          borderLeft: T.border,
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 0 40px rgba(0,0,0,0.12)",
          fontFamily: FF,
        }}
      >
        {/* Panel header */}
        <div
          style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between",
            padding: "13px 20px",
            borderBottom: T.border,
            background: T.bgMuted,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#111827", flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textTertiary, fontFamily: MONO }}>
              Candidate Profile
            </span>
            {target.candidateName && (
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text, background: T.bg, border: T.border, borderRadius: 5, padding: "2px 9px", fontFamily: FF }}>
                {target.candidateName}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 28, height: 28,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: T.bg, border: T.border,
              borderRadius: T.radius - 1, cursor: "pointer",
              color: T.textTertiary, transition: "all 0.12s", outline: "none",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#111827"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#111827" }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.textTertiary; e.currentTarget.style.borderColor = "#e5e7eb" }}
          >
            <X size={13} strokeWidth={2} />
          </button>
        </div>

        {/* ApplicantViewer body */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          <ApplicantViewer
            orgId={target.orgId}
            jobId={target.jobId}
            applicantId={target.applicantId}
            applicantEmail={target.applicantEmail}
          />
        </div>
      </div>
    </>,
    document.body,
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const InterviewsTab = () => {
  const { user } = useAuth()
  const { isAdmin } = useUser()
  const navigate = useNavigate()

  const [allInterviews, setAllInterviews] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [orgMembers, setOrgMembers] = useState([])
  const [showProfileIncompleteOverlay, setShowProfileIncompleteOverlay] =
    useState(false)

  const [showScheduleForm, setShowScheduleForm] = useState(false)
  const [showCalendarView, setShowCalendarView] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showMyInterviews, setShowMyInterviews] = useState(false)
  const [selectedJobs, setSelectedJobs] = useState([])
  const [dateRange, setDateRange] = useState({ from: "", to: "" })

  const [showInterviewDetails, setShowInterviewDetails] = useState(false)
  const [selectedInterviewForDetails, setSelectedInterviewForDetails] =
    useState(null)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedInterviewForDelete, setSelectedInterviewForDelete] =
    useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const [showRescheduleForm, setShowRescheduleForm] = useState(false)
  const [selectedInterviewForReschedule, setSelectedInterviewForReschedule] =
    useState(null)

  const [showScheduleNextModal, setShowScheduleNextModal] = useState(false)
  const [selectedInterviewForNext, setSelectedInterviewForNext] = useState(null)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false,
  )

  // ApplicantViewer panel state
  const [viewingApplicant, setViewingApplicant] = useState(null)

  // Email modal state — holds the interview whose candidate we're emailing
  const [emailTarget, setEmailTarget] = useState(null)

  // Auth guard
  useEffect(() => {
    if (!user?.authenticated) navigate("/login")
  }, [user, navigate])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Data load
  useEffect(() => {
    const load = async () => {
      if (!user?.uid) return
      setIsLoading(true)
      setLoadError(null)
      try {
        const orgData = await OrganizationService.getUserOrganization(user.uid)
        setOrganization(orgData)
        if (orgData?.members)
          setOrgMembers(
            orgData.members.filter(
              (m) => m.role === "interviewer" || m.role === "admin",
            ),
          )
        if (orgData) {
          const data = await interviewTrackingService.getOrganizationInterviews(
            orgData.id,
          )
          setAllInterviews(data)
        }
      } catch {
        setLoadError("Failed to load your data. Please try again later.")
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [user])

  useEffect(() => {
    if (organization)
      setShowProfileIncompleteOverlay(
        !organization.logoUrl || !organization.description,
      )
  }, [organization])

  const onRefreshInterviews = async () => {
    if (!user?.uid || !organization) return
    setIsLoading(true)
    try {
      const data = await interviewTrackingService.getOrganizationInterviews(
        organization.id,
      )
      setAllInterviews(data)
    } catch {
      setLoadError("Failed to load your interviews. Please try again later.")
    } finally {
      setIsLoading(false)
    }
  }

  // Delete handlers
  const handleDeleteInterview = async (ids) => {
    if (!isAdmin) {
      toast.error("User not allowed", { icon: "🔐", duration: 3000 })
      return
    }
    for (const id of ids)
      await interviewTrackingService.deleteInterview(id, organization?.id)
    setAllInterviews((prev) => prev.filter((i) => !ids.includes(i.id)))
  }
  const handleDeleteSingle = async (iv) => {
    setIsDeleting(true)
    try {
      await handleDeleteInterview([iv.id])
      setShowDeleteModal(false)
      setSelectedInterviewForDelete(null)
    } finally {
      setIsDeleting(false)
    }
  }
  const handleDeleteSeries = async (group) => {
    setIsDeleting(true)
    try {
      await handleDeleteInterview(group.map((i) => i.id))
      setShowDeleteModal(false)
      setSelectedInterviewForDelete(null)
    } finally {
      setIsDeleting(false)
    }
  }

  // Reschedule handlers
  const handleRescheduleClick = (iv) => {
    if (!isAdmin) {
      toast.error("Only admins can reschedule", { icon: "⚠️", duration: 3000 })
      return
    }
    setSelectedInterviewForReschedule(iv)
    setShowRescheduleForm(true)
  }
  const handleRescheduleComplete = () => {
    setShowRescheduleForm(false)
    setSelectedInterviewForReschedule(null)
    onRefreshInterviews()
  }

  // Schedule next round
  const handleScheduleNextClick = (iv) => {
    setSelectedInterviewForNext(iv)
    setShowScheduleNextModal(true)
  }
  const handleScheduleNextComplete = () => {
    setShowScheduleNextModal(false)
    setSelectedInterviewForNext(null)
    onRefreshInterviews()
  }

  // Open ApplicantViewer panel
  const handleViewApplicant = (target) => setViewingApplicant(target)

  // Open email modal for a given interview row
  const handleEmailClick = (iv) => setEmailTarget(iv)

  // Derived
  const allJobTitles = useMemo(
    () =>
      [
        ...new Set(
          allInterviews
            .map((i) => i.positionTitle || i.jobTitle)
            .filter(Boolean),
        ),
      ].sort(),
    [allInterviews],
  )

  const filteredInterviews = useMemo(() => {
    let list = [...allInterviews]
    if (selectedJobs.length)
      list = list.filter((i) =>
        selectedJobs.includes(i.positionTitle || i.jobTitle),
      )
    if (dateRange.from || dateRange.to) {
      const from = dateRange.from ? new Date(dateRange.from) : null
      const to = dateRange.to ? new Date(dateRange.to) : null
      if (to) to.setHours(23, 59, 59, 999)
      list = list.filter((i) => {
        const d = new Date(
          i.scheduledDate?.seconds
            ? i.scheduledDate.seconds * 1000
            : i.scheduledDate || 0,
        )
        if (from && d < from) return false
        if (to && d > to) return false
        return true
      })
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (i) =>
          (i.candidateName || i.candidate || "").toLowerCase().includes(q) ||
          (i.interviewerName || i.interviewer || "")
            .toLowerCase()
            .includes(q) ||
          (i.positionTitle || i.jobTitle || "").toLowerCase().includes(q) ||
          (i.title || "").toLowerCase().includes(q),
      )
    }
    if (showMyInterviews && user?.email)
      list = list.filter(
        (i) =>
          i.interviewerEmail === user.email ||
          i.interviewer === user.email ||
          i.interviewerName === user.displayName,
      )
    return list
  }, [
    allInterviews,
    selectedJobs,
    dateRange,
    searchQuery,
    showMyInterviews,
    user,
  ])

  const { upcoming, awaiting, past } = useMemo(
    () => categorise(filteredInterviews),
    [filteredInterviews],
  )
  const hasAny = upcoming.length + awaiting.length + past.length > 0
  const cardRadius = isMobile ? 6 : T.radiusCard

  const openDetails = (iv) => {
    setSelectedInterviewForDetails(iv)
    setShowInterviewDetails(true)
  }

  if (showInterviewDetails && selectedInterviewForDetails) {
    return (
      <InterviewDetailsPage
        interview={selectedInterviewForDetails}
        onBack={() => {
          setShowInterviewDetails(false)
          setSelectedInterviewForDetails(null)
        }}
        onRefreshInterviews={onRefreshInterviews}
        interviewers={orgMembers}
      />
    )
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bgPage, fontFamily: FF }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes ivt-spin { to { transform: rotate(360deg); } }
        .ivt-scroll::-webkit-scrollbar { display: none; }
        .ivt-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div style={{ display: "flex" }}>
        {/* Sidebar */}
        <div
          style={{
            borderRight: T.border,
            background: T.bg,
            minHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <ProfileSidebar
            activeTab="interviews"
            setActiveTab={() => {}}
            showMobileHeader={false}
          />
        </div>

        {/* Main */}
        <div
          className="ivt-scroll"
          style={{
            flex: 1,
            marginLeft: isMobile ? 0 : DESKTOP_SIDEBAR_WIDTH,
            overflowY: "auto",
            width: "100%",
          }}
        >
          <div
            style={{
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Top bar */}
            <div
              style={{
                background: T.bg,
                borderBottom: T.border,
                padding: isMobile ? "14px 16px" : "13px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: isMobile ? "flex-start" : "space-between",
                flexShrink: 0,
                position: "sticky",
                top: 0,
                zIndex: 20,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 0 : 10 }}>
                {!isMobile && (
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#111827",
                      flexShrink: 0,
                    }}
                  />
                )}
                <span
                  style={{
                    fontSize: isMobile ? 18 : 12,
                    fontWeight: 700,
                    textTransform: isMobile ? "none" : "uppercase",
                    letterSpacing: isMobile ? "-0.02em" : "0.08em",
                    color: isMobile ? T.text : T.textTertiary,
                    fontFamily: isMobile ? FF : MONO,
                  }}
                >
                  Interviews
                </span>
              </div>

              {!isMobile && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <MineToggle
                    value={showMyInterviews}
                    onChange={setShowMyInterviews}
                  />
                  <JobFilterDropdown
                    allJobTitles={allJobTitles}
                    allInterviews={allInterviews}
                    selectedJobs={selectedJobs}
                    setSelectedJobs={setSelectedJobs}
                  />
                  <DateRangePicker
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                  />

                  {/* Search */}
                  <div style={{ position: "relative", height: 34 }}>
                    <div
                      style={{
                        position: "absolute",
                        left: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Search size={13} color={T.textTertiary} />
                    </div>
                    <input
                      type="text"
                      placeholder="Search candidates, roles…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        height: "100%",
                        paddingLeft: 30,
                        paddingRight: searchQuery ? 28 : 12,
                        width: 210,
                        border: T.border,
                        borderRadius: T.radius,
                        fontSize: 13,
                        fontWeight: 500,
                        color: T.text,
                        fontFamily: FF,
                        background: T.bg,
                        outline: "none",
                        transition: "border-color 0.12s",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#9ca3af"
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e5e7eb"
                      }}
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        style={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: T.textTertiary,
                          display: "flex",
                          alignItems: "center",
                          padding: 0,
                          outline: "none",
                        }}
                      >
                        <X size={11} />
                      </button>
                    )}
                  </div>

                  <ViewToggle
                    isCalendar={showCalendarView}
                    onChange={setShowCalendarView}
                  />

                  <button
                    onClick={() => setShowScheduleForm(true)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      height: 34,
                      padding: "0 14px",
                      borderRadius: T.radius,
                      border: "none",
                      background: "#111827",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: FF,
                      cursor: "pointer",
                      transition: "background 0.12s",
                      outline: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#27272a"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#111827"
                    }}
                  >
                    <Plus size={12} strokeWidth={2.5} />
                    New interview
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="ivt-scroll" style={{ flex: 1, overflowY: "auto" }}>
              {showCalendarView ? (
                <div style={{ background: T.bg, height: "100%" }}>
                  {isLoading ? (
                    <CalendarShimmer />
                  ) : (
                    <CalendarScheduling
                      organizationData={organization}
                      interviewersData={orgMembers}
                      allInterviews={allInterviews}
                    />
                  )}
                </div>
              ) : (
                <div style={{ padding: isMobile ? "12px 12px 40px" : "20px 20px 48px" }}>
                  {isLoading ? (
                    <div
                      style={{
                        background: T.bg,
                        borderRadius: cardRadius,
                        border: T.border,
                        boxShadow: T.shadow,
                        overflowX: "auto",
                        overflowY: "hidden",
                      }}
                    >
                      <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                        <TableHeader />
                        <TableShimmer rows={6} />
                      </div>
                    </div>
                  ) : loadError ? (
                    <ErrorState
                      error={loadError}
                      onRetry={onRefreshInterviews}
                      cardRadius={cardRadius}
                    />
                  ) : !hasAny ? (
                    <EmptyState
                      searchQuery={searchQuery}
                      onSchedule={() => setShowScheduleForm(true)}
                      cardRadius={cardRadius}
                    />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                      }}
                    >
                      {[
                        { key: "upcoming", data: upcoming },
                        { key: "awaiting", data: awaiting },
                        { key: "past", data: past },
                      ].map(({ key, data }) => (
                        <SectionTable
                          key={key}
                          sectionKey={key}
                          interviews={data}
                          onCardClick={openDetails}
                          onDelete={(iv) => {
                            setSelectedInterviewForDelete(iv)
                            setShowDeleteModal(true)
                          }}
                          onReschedule={handleRescheduleClick}
                          onScheduleNext={handleScheduleNextClick}
                          navigate={navigate}
                          onViewApplicant={handleViewApplicant}
                          onEmailClick={handleEmailClick}
                          cardRadius={cardRadius}
                        />
                      ))}
                    </div>
                  )}

                  {!isLoading && !loadError && hasAny && (
                    <div style={{ marginTop: 12, paddingLeft: 4 }}>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: T.textTertiary,
                          fontFamily: FF,
                        }}
                      >
                        {filteredInterviews.length} of {allInterviews.length}{" "}
                        {allInterviews.length === 1
                          ? "interview"
                          : "interviews"}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Schedule New Interview */}
      {showScheduleForm && (
        <OrgScheduleInterviewForm
          isOpen={showScheduleForm}
          onClose={() => setShowScheduleForm(false)}
          onScheduleComplete={() => {
            setShowScheduleForm(false)
            onRefreshInterviews()
          }}
          organizationData={organization}
          interviewersData={orgMembers}
        />
      )}

      {/* Delete Modal */}
      <DeleteInterviewModal
        isOpen={showDeleteModal}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteModal(false)
            setSelectedInterviewForDelete(null)
          }
        }}
        interview={selectedInterviewForDelete}
        interviewGroup={
          selectedInterviewForDelete
            ? allInterviews.filter(
                (i) => i.traceId === selectedInterviewForDelete?.traceId,
              )
            : []
        }
        onDeleteSingle={handleDeleteSingle}
        onDeleteSeries={handleDeleteSeries}
        isDeleting={isDeleting}
      />

      {/* Reschedule Modal */}
      {showRescheduleForm && selectedInterviewForReschedule && (
        <RescheduleInterviewForm
          isOpen={showRescheduleForm}
          onClose={() => {
            setShowRescheduleForm(false)
            setSelectedInterviewForReschedule(null)
          }}
          interview={selectedInterviewForReschedule}
          onRescheduleComplete={handleRescheduleComplete}
          interviewersData={orgMembers}
          orgId={organization?.id}
        />
      )}

      {/* Schedule Next Round Modal */}
      {showScheduleNextModal && selectedInterviewForNext && (
        <ScheduleNextRoundModal
          isOpen={showScheduleNextModal}
          onClose={() => {
            setShowScheduleNextModal(false)
            setSelectedInterviewForNext(null)
          }}
          interview={selectedInterviewForNext}
          onScheduleComplete={handleScheduleNextComplete}
          interviewersData={orgMembers}
          orgId={organization?.id}
        />
      )}

      {/* Profile Incomplete Overlay */}
      <ProfileIncompleteOverlay
        isOpen={showProfileIncompleteOverlay}
        onClose={() => navigate("/profile")}
        onComplete={() => navigate("/profile")}
      />

      {/* ApplicantViewer slide-over */}
      {viewingApplicant && (
        <ApplicantPanel
          target={viewingApplicant}
          onClose={() => setViewingApplicant(null)}
        />
      )}

      {/* Email Modal */}
      {emailTarget && (
        <EmailModal
          isOpen={true}
          onClose={() => setEmailTarget(null)}
          organizationId={emailTarget.organizationId ?? organization?.id ?? ""}
          applicantId={emailTarget.applicantId ?? ""}
          senderId={user?.uid ?? ""}
          candidateName={emailTarget.candidateName || emailTarget.candidate || ""}
          candidateEmail={emailTarget.candidateEmail ?? ""}
        />
      )}
    </div>
  )
}

export default InterviewsTab
